import { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { NUM, fmtDate, sameOrNew } from "@/lib/format";
import { PageHeader, StatCard, Empty } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusCircle, Trash2, Search, Check, PackagePlus, Tag, Clock, Truck, CheckCircle2, FileSpreadsheet, ArrowRight, User, ScanLine, Pencil, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import BulkOrderDialog from "@/components/BulkOrderDialog";
import OrderEditDialog from "@/components/OrderEditDialog";
import OrderFormBuilder from "@/components/orders/OrderFormBuilder";
import OrderFormsList from "@/components/orders/OrderFormsList";
import OrderFormPreviewDialog from "@/components/orders/OrderFormPreviewDialog";

const STATUS = {
  bekliyor: { label: "Bekliyor", stripe: "border-l-amber-500", pill: "bg-amber-50 text-amber-800 border-amber-200", head: "border-t-amber-500", count: "bg-amber-100 text-amber-800", icon: Clock, title: "Bekleyen Talepler", tone: "warning" },
  siparis_verildi: { label: "Sipariş Verildi", stripe: "border-l-blue-500", pill: "bg-blue-50 text-blue-800 border-blue-200", head: "border-t-blue-500", count: "bg-blue-100 text-blue-800", icon: Truck, title: "Sipariş Verildi / Yolda", tone: "blue" },
  tamamlandi: { label: "Tamamlandı", stripe: "border-l-emerald-500", pill: "bg-emerald-50 text-emerald-800 border-emerald-200", head: "border-t-emerald-500", count: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, title: "Tamamlandı / Stoğa Alındı", tone: "success" },
};
const COLS = ["bekliyor", "siparis_verildi", "tamamlandi"];
const nextStatus = { bekliyor: "siparis_verildi", siparis_verildi: "tamamlandi" };
const emptyForm = { product_id: "", product_name: "", brand: "", code: "", requested_qty: 1, note: "" };
const UNITS = ["ADET", "METRE", "KİLO", "PAKET", "KUTU", "TOP", "RULO", "METRETÜL", "TAKIM", "ÇİFT"];
const orderNo = (o) => `#${(o.id || "").slice(0, 6).toUpperCase()}`;

export default function Orders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [pSearch, setPSearch] = useState("");
  const [stockOrder, setStockOrder] = useState(null);
  const [stockForm, setStockForm] = useState({ quantity: 1, unit: "ADET", category: "", price: 0, cost: 0 });
  const [forms, setForms] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [previewForm, setPreviewForm] = useState(null);

  const loadForms = () => { if (isAdmin) api.get("/order-forms").then((r) => setForms(sameOrNew(r.data))).catch(() => {}); };
  const load = () => { api.get("/orders").then((r) => setOrders(sameOrNew(r.data))).catch(() => {}); loadForms(); };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []); // eslint-disable-line
  useAutoRefresh(load);
  const openOrders = useMemo(() => orders.filter((o) => o.status === "bekliyor"), [orders]);
  const openFormBuilder = (f = null) => { setEditForm(f); setFormOpen(true); };

  const matches = useMemo(() => {
    if (!pSearch.trim()) return [];
    const s = pSearch.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(s) || (p.code || "").toLowerCase().includes(s) || (p.brand || "").toLowerCase().includes(s) || (p.barcode || "").toLowerCase().includes(s)).slice(0, 6);
  }, [products, pSearch]);

  const isInStock = !!form.product_id;
  const pick = (p) => { setForm({ ...form, product_id: p.id, product_name: p.name, brand: p.brand || "", code: p.code || "" }); setPSearch(p.name); };
  const clearPick = () => { setForm({ ...form, product_id: "", product_name: "", brand: "", code: "" }); setPSearch(""); };

  const create = async () => {
    const name = form.product_id ? form.product_name : pSearch.trim();
    if (!name) return toast.error("Ürün adı gerekli");
    try {
      await api.post("/orders", { ...form, product_name: name, requested_qty: Number(form.requested_qty) });
      toast.success("Sipariş talebi iletildi");
      setDlg(false); setForm(emptyForm); setPSearch(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, status) => {
    const { data } = await api.put(`/orders/${id}`, { status });
    toast.success(data?.stocked ? `Tamamlandı — stoğa +${data.added_qty} adet işlendi` : "Durum güncellendi");
    load();
  };
  const del = async (id) => { await api.delete(`/orders/${id}`); load(); };

  const openStock = (o) => { setStockForm({ quantity: o.requested_qty || 1, unit: "ADET", category: "", price: 0, cost: 0 }); setStockOrder(o); };
  const stockIt = async () => {
    try {
      await api.post(`/orders/${stockOrder.id}/stock`, {
        quantity: Number(stockForm.quantity) || 0, unit: stockForm.unit, category: stockForm.category,
        price: Number(stockForm.price) || 0, cost: Number(stockForm.cost) || 0,
      });
      toast.success("Ürün stoğa eklendi");
      setStockOrder(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openNew = () => { setForm(emptyForm); setPSearch(""); setDlg(true); };

  const exportExcel = () => {
    if (!orders.length) return toast.error("Dışa aktarılacak talep yok");
    const rows = orders.map((o) => ({ "Talep No": orderNo(o), "Tarih": fmtDate(o.created_at), "Ürün": o.product_name, "Marka": o.brand || "", "Kod": o.code || "", "Miktar": o.requested_qty, "Durum": STATUS[o.status]?.label || o.status, "Talep Eden": o.user_name || "", "Stokta": o.in_stock ? "Evet" : "Hayır (stoğa dahil edilecek)", "Not": o.note || "" }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 10 }, { wch: 12 }, { wch: 40 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 16 }, { wch: 18 }, { wch: 26 }, { wch: 40 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Sipariş Talepleri");
    XLSX.writeFile(wb, "siparis-talepleri.xlsx");
    toast.success(`${rows.length} talep Excel'e aktarıldı`);
  };

  const counts = useMemo(() => COLS.reduce((m, k) => ({ ...m, [k]: orders.filter((o) => o.status === k).length }), {}), [orders]);
  const newProducts = orders.filter((o) => !o.in_stock).length;

  const OrderCard = ({ o }) => {
    const S = STATUS[o.status] || STATUS.bekliyor;
    return (
      <div className={cn("relative bg-card border border-l-4 rounded-r-lg rounded-l-sm px-2.5 py-2 shadow-sm hover:shadow-md transition-shadow", S.stripe)} data-testid={`order-${o.id}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0 text-[10px]">
            <span className="font-mono font-bold text-slate-500" data-testid={`order-no-${o.id}`}>{orderNo(o)}</span>
            <span className="text-muted-foreground tnum">{fmtDate(o.created_at)}</span>
            {!o.in_stock && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0 text-[8px] gap-0.5 px-1 py-0"><PackagePlus className="h-2 w-2" />Stoğa dahil</Badge>}
            {o.stocked && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 text-[8px] gap-0.5 px-1 py-0"><Check className="h-2 w-2" />Stoğa eklendi</Badge>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {(isAdmin || (o.user_id === user?.id && o.status === "bekliyor")) && !o.qty_applied && (
              <button onClick={() => setEditOrder(o)} className="text-muted-foreground hover:text-primary transition-colors p-0.5" title="Talebi düzenle (miktar / ürün)" data-testid={`order-edit-${o.id}`}><Pencil className="h-3 w-3" /></button>
            )}
            {isAdmin && <button onClick={() => del(o.id)} className="text-muted-foreground hover:text-destructive transition-colors p-0.5" title="Talebi sil" data-testid={`order-del-${o.id}`}><Trash2 className="h-3 w-3" /></button>}
          </div>
        </div>
        <div className="mt-1 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-[12px] leading-snug line-clamp-2" title={o.product_name}>{o.product_name}</div>
            <div className="flex items-center gap-1.5 flex-wrap mt-0.5 text-[10px]">
              {o.brand && <span className="inline-flex items-center gap-0.5 rounded-full bg-secondary/10 text-secondary px-1.5 font-semibold"><Tag className="h-2 w-2" />{o.brand}</span>}
              {o.code && <span className="font-mono text-muted-foreground">{o.code}</span>}
              <span className="text-muted-foreground inline-flex items-center gap-0.5 truncate"><User className="h-2.5 w-2.5" />{o.user_name || "—"}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground leading-none">Miktar</div>
            <div className="font-head font-black tnum text-sm leading-tight">{NUM(o.requested_qty)}</div>
          </div>
        </div>
        {o.note && <div className="text-[10px] text-muted-foreground mt-1 italic border-l-2 border-slate-200 pl-1.5 line-clamp-2" title={o.note}>"{o.note}"</div>}
        {isAdmin && (o.status !== "tamamlandi" || (!o.in_stock && !o.stocked)) && (
          <div className="flex gap-1.5 mt-1.5">
            {o.status !== "tamamlandi" && (
              <Button size="sm" variant="outline" className="flex-1 h-7 text-[11px]" onClick={() => setStatus(o.id, nextStatus[o.status])} data-testid={`order-advance-${o.id}`}>
                {o.status === "bekliyor" ? "Sipariş Ver" : "Tamamlandı"}<ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            )}
            {!o.in_stock && !o.stocked && (
              <Button size="sm" className="flex-1 h-7 text-[11px] bg-secondary hover:bg-secondary/90 text-white" onClick={() => openStock(o)} data-testid={`order-stock-${o.id}`}>
                <PackagePlus className="h-3 w-3 mr-1" />Stoğa Ekle
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 stagger-in" data-testid="orders-page">
      <PageHeader title="Sipariş Talepleri" subtitle={isAdmin ? "Depodan gelen eksik ürün talepleri · sipariş ve stok tedarik panosu" : "Sipariş edilmesi gereken ürünleri iletin; durumunu buradan izleyin"} badge="Depo & Satın Alma">
        {isAdmin && <Button variant="outline" size="sm" onClick={exportExcel} data-testid="orders-export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />Excel'e Aktar</Button>}
        {isAdmin && <Button variant="outline" size="sm" className="border-[#0B192C]/30 bg-[#0B192C] text-white hover:bg-[#0B192C]/90 hover:text-white" onClick={() => openFormBuilder(null)} data-testid="order-form-create-btn"><ClipboardList className="h-4 w-4 mr-2 text-[#ffb27a]" />Sipariş Çıktısı Oluştur</Button>}
        <Button variant="secondary" size="sm" className="text-white" onClick={() => setBulkOpen(true)} data-testid="bulk-order-btn"><ScanLine className="h-4 w-4 mr-2" />Toplu Talep (Barkod)</Button>
        <Button size="sm" onClick={openNew} data-testid="add-order-btn"><PlusCircle className="h-4 w-4 mr-2" />Yeni Talep Oluştur</Button>
      </PageHeader>
      <BulkOrderDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
      <OrderEditDialog order={editOrder} onClose={() => setEditOrder(null)} onSaved={load} />
      {isAdmin && <OrderFormBuilder open={formOpen} onOpenChange={setFormOpen} initial={editForm} openOrders={openOrders} onSaved={load} />}
      {isAdmin && <OrderFormPreviewDialog form={previewForm} onClose={() => setPreviewForm(null)} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard size="sm" label="Bekleyen" value={NUM(counts.bekliyor)} icon={Clock} tone="warning" sub="sipariş bekliyor" testid="orders-kpi-pending" />
        <StatCard size="sm" label="Sipariş Verildi" value={NUM(counts.siparis_verildi)} icon={Truck} tone="blue" sub="yolda / tedarikte" testid="orders-kpi-ordered" />
        <StatCard size="sm" label="Tamamlandı" value={NUM(counts.tamamlandi)} icon={CheckCircle2} tone="success" sub="stoğa alındı" testid="orders-kpi-completed" />
        <StatCard size="sm" label="Stoğa Dahil Edilecek" value={NUM(newProducts)} icon={PackagePlus} tone="primary" sub="kataloğa yeni ürün" testid="orders-kpi-new-products" />
      </div>

      {orders.length === 0 ? (
        <Empty title="Henüz talep yok" desc="Eksik ürünler için yeni bir talep oluşturun." action={<Button size="sm" onClick={openNew} data-testid="orders-empty-add-btn"><PlusCircle className="h-4 w-4 mr-1.5" />Yeni Talep</Button>} />
      ) : (
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 md:grid md:grid-cols-3 md:overflow-visible md:snap-none" data-testid="orders-kanban">
          {COLS.map((key) => {
            const S = STATUS[key]; const Icon = S.icon;
            const colOrders = orders.filter((o) => o.status === key);
            return (
              <section key={key} className={cn("min-w-[86vw] sm:min-w-[340px] md:min-w-0 snap-start bg-slate-50/80 border border-slate-200 rounded-xl border-t-4 flex flex-col", S.head)} data-testid={`order-col-${key}`}>
                <header className="flex items-center justify-between px-3 py-2 border-b border-slate-200/70">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Icon className="h-3.5 w-3.5 text-slate-600 shrink-0" />
                    <span className="font-head text-[11px] font-bold uppercase tracking-wider text-slate-600 truncate">{S.title}</span>
                  </div>
                  <span className={cn("h-5 min-w-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center tnum", S.count)} data-testid={`order-col-count-${key}`}>{colOrders.length}</span>
                </header>
                <div className="p-2 space-y-1.5 min-h-[120px] flex-1 max-h-[calc(100vh-330px)] overflow-y-auto">
                  {colOrders.length === 0 ? (
                    <div className="h-full min-h-[90px] flex items-center justify-center text-xs text-muted-foreground border border-dashed rounded-lg">Bu sütunda talep yok</div>
                  ) : colOrders.map((o) => <OrderCard key={o.id} o={o} />)}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {isAdmin && <OrderFormsList forms={forms} onEdit={(f) => openFormBuilder(f)} onPreview={(f) => setPreviewForm(f)} onChanged={load} />}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md" data-testid="order-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Yeni Sipariş Talebi Gir</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Mevcut Stoktan Ara veya Yeni Ürün Gir</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" placeholder="Stoktan ara veya yeni ürün adı yaz..." value={pSearch}
                  onChange={(e) => { setPSearch(e.target.value); if (form.product_id) setForm({ ...form, product_id: "", brand: "", code: "" }); }}
                  data-testid="order-search" />
              </div>
              {pSearch.trim() && !isInStock && (
                <div className="border rounded-lg overflow-hidden">
                  {matches.length > 0 ? matches.map((p) => (
                    <button key={p.id} onClick={() => pick(p)} className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between border-b last:border-0 transition-colors" data-testid={`order-pick-${p.id}`}>
                      <div>
                        <div className="text-sm font-semibold">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{[p.brand, p.code].filter(Boolean).join(" · ") || "—"}</div>
                      </div>
                      <span className="text-xs text-muted-foreground tnum">{NUM(p.quantity)} {p.unit}</span>
                    </button>
                  )) : (
                    <div className="px-3 py-3 text-sm flex items-center gap-2 text-primary bg-primary/5">
                      <PackagePlus className="h-4 w-4 shrink-0" /> <span>Bu ürün stokta yok — <b>stoğa dahil edilecek</b> olarak iletilecek.</span>
                    </div>
                  )}
                </div>
              )}
              {isInStock && (
                <div className="flex items-center justify-between bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                  <span className="flex items-center gap-2"><Check className="h-4 w-4" />Stoktan seçildi: {form.product_name}</span>
                  <button onClick={clearPick} className="text-xs underline" data-testid="order-clear-pick">değiştir</button>
                </div>
              )}
            </div>

            {!isInStock && pSearch.trim() && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Marka</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="order-brand" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ürün Kodu</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="order-code" />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Talep Miktarı</Label>
              <Input type="number" className="tnum" value={form.requested_qty} onChange={(e) => setForm({ ...form, requested_qty: e.target.value })} data-testid="order-qty" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Notlar</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="order-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} data-testid="order-cancel">İptal</Button>
            <Button onClick={create} data-testid="save-order">Talebi İlet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stockOrder} onOpenChange={(o) => !o && setStockOrder(null)}>
        <DialogContent className="max-w-md" data-testid="order-stock-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Stoğa Ekle</DialogTitle></DialogHeader>
          {stockOrder && (
            <div className="space-y-4">
              <div className="rounded-lg border-l-4 border-l-primary bg-slate-50 px-3 py-2.5">
                <div className="font-semibold text-sm">{stockOrder.product_name}</div>
                <div className="text-xs text-muted-foreground">{[stockOrder.brand, stockOrder.code].filter(Boolean).join(" · ") || "—"} · Talep {NUM(stockOrder.requested_qty)}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Başlangıç Stoğu</Label>
                  <Input type="number" className="tnum" value={stockForm.quantity} onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })} data-testid="stock-qty" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Birim</Label>
                  <Select value={stockForm.unit} onValueChange={(v) => setStockForm({ ...stockForm, unit: v })}>
                    <SelectTrigger data-testid="stock-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kategori</Label>
                  <Input value={stockForm.category} onChange={(e) => setStockForm({ ...stockForm, category: e.target.value })} placeholder="Opsiyonel" data-testid="stock-category" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Satış Fiyatı (₺)</Label>
                  <Input type="number" className="tnum" value={stockForm.price} onChange={(e) => setStockForm({ ...stockForm, price: e.target.value })} data-testid="stock-price" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockOrder(null)} data-testid="stock-cancel">İptal</Button>
            <Button onClick={stockIt} data-testid="save-stock">Stoğa Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
